type GoogleTokenResponse = { access_token?: string; error?: string };
type GoogleTokenClient = { requestAccessToken: (options: { prompt: string }) => void };
type PickerDocument = { id: string; name: string; resourceKey?: string };

type GoogleWindow = Window & {
  google?: {
    accounts: { oauth2: { initTokenClient: (options: { client_id: string; scope: string; callback: (response: GoogleTokenResponse) => void }) => GoogleTokenClient } };
    picker: {
      Action: { PICKED: string; CANCEL: string };
      DocsView: new (viewId: string) => { setEnableDrives: (value: boolean) => unknown; setIncludeFolders: (value: boolean) => unknown; setOwnedByMe: (value: boolean) => unknown; setSelectFolderEnabled: (value: boolean) => unknown; setMimeTypes: (value: string) => unknown };
      PickerBuilder: new () => {
        addView: (view: unknown) => unknown;
        enableFeature: (feature: string) => unknown;
        setOAuthToken: (token: string) => unknown;
        setDeveloperKey: (key: string) => unknown;
        setAppId: (appId: string) => unknown;
        setOrigin: (origin: string) => unknown;
        setCallback: (callback: (data: { action?: string; docs?: PickerDocument[] }) => void) => unknown;
        build: () => { setVisible: (visible: boolean) => void };
      };
      Feature: { SUPPORT_DRIVES: string };
      ViewId: { FOLDERS: string };
    };
  };
  gapi?: { load: (name: string, options: { callback: () => void; onerror: () => void }) => void };
};

const driveScope = 'https://www.googleapis.com/auth/drive.readonly';
const folderMimeType = 'application/vnd.google-apps.folder';
const maxDriveFileBytes = 10 * 1024 * 1024;
const freePlanDriveBytes = 1024 * 1024;
const freePlanDriveFiles = 25;
const freePlanDriveFoldersScanned = 100;
const freePlanDriveCandidates = 2500;
const scriptPromises = new Map<string, Promise<void>>();

function loadScript(src: string) {
  const existing = scriptPromises.get(src);
  if (existing) return existing;
  const promise = new Promise<void>((resolve, reject) => {
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('Google Drive could not be loaded.'));
    document.head.append(script);
  });
  scriptPromises.set(src, promise);
  return promise;
}

async function loadGooglePicker() {
  await loadScript('https://apis.google.com/js/api.js');
  const googleWindow = window as GoogleWindow;
  if (!googleWindow.gapi) throw new Error('Google Picker could not be loaded.');
  await new Promise<void>((resolve, reject) => googleWindow.gapi?.load('picker', { callback: resolve, onerror: reject }));
}

export async function requestGoogleDriveAccess() {
  const clientId = import.meta.env.VITE_GOOGLE_DRIVE_CLIENT_ID;
  if (!clientId) throw new Error('Google Drive access is not configured.');
  await loadScript('https://accounts.google.com/gsi/client');
  const googleWindow = window as GoogleWindow;
  if (!googleWindow.google?.accounts) throw new Error('Google Drive authorization could not be loaded.');
  return new Promise<string>((resolve, reject) => {
    const client = googleWindow.google?.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: driveScope,
      callback: (response) => {
        if (response.access_token) resolve(response.access_token);
        else reject(new Error(response.error || 'Google Drive access was not granted.'));
      },
    });
    client?.requestAccessToken({ prompt: 'select_account' });
  });
}

export async function pickGoogleDriveFolder(accessToken: string) {
  const developerKey = import.meta.env.VITE_GOOGLE_DRIVE_API_KEY;
  if (!developerKey) throw new Error('Google Drive Picker is not configured.');
  await loadGooglePicker();
  const googleWindow = window as GoogleWindow;
  const picker = googleWindow.google?.picker;
  if (!picker) throw new Error('Google Picker could not be loaded.');
  return new Promise<PickerDocument>((resolve, reject) => {
    const view = new picker.DocsView(picker.ViewId.FOLDERS);
    view.setEnableDrives(true);
    view.setIncludeFolders(true);
    view.setOwnedByMe(false);
    view.setSelectFolderEnabled(true);
    view.setMimeTypes(folderMimeType);
    const builder = new picker.PickerBuilder();
    builder.addView(view);
    builder.enableFeature(picker.Feature.SUPPORT_DRIVES);
    builder.setOAuthToken(accessToken);
    builder.setDeveloperKey(developerKey);
    builder.setOrigin(window.location.origin);
    if (import.meta.env.VITE_GOOGLE_DRIVE_APP_ID) builder.setAppId(import.meta.env.VITE_GOOGLE_DRIVE_APP_ID);
    builder.setCallback((data) => {
      if (data.action === picker.Action.PICKED && data.docs?.[0]) resolve(data.docs[0]);
      else if (data.action === picker.Action.CANCEL) reject(new Error('Google Drive selection was cancelled.'));
    });
    builder.build().setVisible(true);
  });
}

type DriveFile = {
  id: string;
  name: string;
  mimeType: string;
  size?: string;
  createdTime?: string;
  modifiedTime?: string;
  resourceKey?: string;
  shortcutDetails?: { targetId: string; targetMimeType: string; targetResourceKey?: string };
};

export type DriveFolderReadProgress = {
  currentFileName: string;
  currentFileNumber: number;
  totalFiles: number;
};

function fileName(name: string, extension: string) {
  return name.toLowerCase().endsWith(extension) ? name : `${name}${extension}`;
}

function isSupportedDriveFile(source: DriveFile) {
  return [
    'application/pdf',
    'application/vnd.google-apps.document',
    'application/vnd.google-apps.presentation',
    'application/vnd.google-apps.spreadsheet',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    'text/markdown',
    'text/plain',
  ].includes(source.mimeType);
}

async function downloadDriveFile(accessToken: string, source: DriveFile, remainingBytes: number) {
  let url = `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(source.id)}?alt=media&supportsAllDrives=true`;
  let contentType = source.mimeType;
  let name = source.name;
  if (source.mimeType === 'application/vnd.google-apps.document') {
    contentType = 'text/plain';
    name = fileName(name, '.txt');
    url = `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(source.id)}/export?mimeType=${encodeURIComponent(contentType)}`;
  } else if (source.mimeType === 'application/vnd.google-apps.spreadsheet' || source.mimeType === 'application/vnd.google-apps.presentation') {
    contentType = 'application/pdf';
    name = fileName(name, '.pdf');
    url = `https://www.googleapis.com/drive/v3/files/${encodeURIComponent(source.id)}/export?mimeType=${encodeURIComponent(contentType)}`;
  } else if (contentType === 'application/pdf') name = fileName(name, '.pdf');
  else if (contentType === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document') name = fileName(name, '.docx');
  else if (contentType === 'text/markdown') name = fileName(name, '.md');
  else if (contentType === 'text/plain') {
    if (!/\.(?:txt|md)$/i.test(name)) name = fileName(name, '.txt');
  }
  else return null;

  const headers = { Authorization: `Bearer ${accessToken}`, ...(source.resourceKey ? { 'X-Goog-Drive-Resource-Keys': `${source.id}/${source.resourceKey}` } : {}) };
  const response = await fetch(url, { headers });
  if (!response.ok) throw new Error(`Could not read ${source.name} from Google Drive.`);
  const declaredSize = Number(response.headers.get('content-length'));
  if (Number.isFinite(declaredSize) && declaredSize > remainingBytes) return null;
  const blob = await response.blob();
  if (!blob.size || blob.size > maxDriveFileBytes) throw new Error(`${source.name} is empty or larger than 10 MiB.`);
  if (blob.size > remainingBytes) return null;
  return new File([blob], name, { type: contentType });
}

export async function getGoogleDriveFolderFiles(
  accessToken: string,
  folderId: string,
  folderResourceKey?: string,
  onProgress?: (progress: DriveFolderReadProgress) => void,
) {
  const folders: Array<{ id: string; resourceKey?: string }> = [{ id: folderId, resourceKey: folderResourceKey }];
  const visited = new Set<string>();
  const candidates: DriveFile[] = [];
  while (folders.length && visited.size < freePlanDriveFoldersScanned && candidates.length < freePlanDriveCandidates) {
    const folder = folders.shift();
    if (!folder || visited.has(folder.id)) continue;
    visited.add(folder.id);
    let pageToken = '';
    do {
      const query = new URLSearchParams({
        q: `'${folder.id.replace(/'/g, "\\'")}' in parents and trashed = false`,
        pageSize: '100',
        orderBy: 'createdTime desc',
        fields: 'nextPageToken,files(id,name,mimeType,size,createdTime,modifiedTime,resourceKey,shortcutDetails(targetId,targetMimeType,targetResourceKey))',
        includeItemsFromAllDrives: 'true',
        supportsAllDrives: 'true',
      });
      if (pageToken) query.set('pageToken', pageToken);
      const headers = { Authorization: `Bearer ${accessToken}`, ...(folder.resourceKey ? { 'X-Goog-Drive-Resource-Keys': `${folder.id}/${folder.resourceKey}` } : {}) };
      const response = await fetch(`https://www.googleapis.com/drive/v3/files?${query}`, { headers });
      if (!response.ok) throw new Error('The selected Google Drive folder could not be read.');
      const result = await response.json() as { files?: DriveFile[]; nextPageToken?: string };
      for (const source of result.files || []) {
        if (candidates.length >= freePlanDriveCandidates) break;
        if (source.mimeType === folderMimeType) {
          folders.push({ id: source.id, resourceKey: source.resourceKey });
          continue;
        }
        if (source.mimeType === 'application/vnd.google-apps.shortcut' && source.shortcutDetails) {
          const target = source.shortcutDetails;
          if (target.targetMimeType === folderMimeType) folders.push({ id: target.targetId, resourceKey: target.targetResourceKey });
          else {
            const targetSource = { ...source, id: target.targetId, mimeType: target.targetMimeType, resourceKey: target.targetResourceKey };
            if (isSupportedDriveFile(targetSource)) candidates.push(targetSource);
          }
          continue;
        }
        if (isSupportedDriveFile(source)) candidates.push(source);
      }
      pageToken = result.nextPageToken || '';
    } while (pageToken && candidates.length < freePlanDriveCandidates);
  }
  const newestCandidates = candidates
    .sort((a, b) => (Date.parse(b.createdTime || '') || 0) - (Date.parse(a.createdTime || '') || 0))
    .slice(0, freePlanDriveFiles);
  const files: File[] = [];
  let totalBytes = 0;
  for (const [index, source] of newestCandidates.entries()) {
    if (totalBytes >= freePlanDriveBytes) break;
    onProgress?.({
      currentFileName: source.name,
      currentFileNumber: index + 1,
      totalFiles: newestCandidates.length,
    });
    const file = await downloadDriveFile(accessToken, source, freePlanDriveBytes - totalBytes);
    if (!file) continue;
    files.push(file);
    totalBytes += file.size;
  }
  if (!files.length) throw new Error('The selected folder has no supported PDF, DOCX, TXT, Markdown, Docs, Sheets, or Slides files.');
  return {
    files,
    totalBytes,
    skippedCount: Math.max(0, newestCandidates.length - files.length),
    scanTruncated: folders.length > 0 || candidates.length >= freePlanDriveCandidates,
  };
}
